import React, {FC, useEffect} from 'react';

import {useFocusOnElement} from '@optimaxdev/analytics/desktop/hooks/useFocusOnElement/useFocusOnElement';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';

import {CtaButton} from '@optimaxdev/design-system';
import {Icon} from '@optimaxdev/icons';
import classNames from 'classnames';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {AMAZON_URL_PARAM} from 'desktop/pages/checkout/constants/amazon';

import {deleteParam} from 'libs/url/url';

import * as AmazonIcon from '../../../../img/amazonPay.svg';

import s from './amazonButtons.scss';

export type PropsType = {
    hasError: boolean;
    isPaymentConfirmed: boolean;
    isPaymentReady: boolean;
    isLoadingSendOrderButton: boolean;
    onSave: () => void;
    onSend: () => void;
};

/**
 * Render Send Amazon Payment button or Save Amazon Payment button
 */
export const AmazonButtons: FC<PropsType> = React.memo(
    ({hasError, isLoadingSendOrderButton, isPaymentConfirmed, isPaymentReady, onSave, onSend}) => {
        const isFacelift = useFeature('dCheckoutFacelift');

        const handleReturnDeliveryStep = () => {
            deleteParam(AMAZON_URL_PARAM.PAYMENT_CONFIRMED, true);
            deleteParam(AMAZON_URL_PARAM.SESSION_ID, true);
            window.location.reload();
        };

        const {handler, ref} = useFocusOnElement({
            callBack: () => {
                sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                    message: 'CTA - Place Order - Amazon pay - Hover',
                    method: 'Payment',
                });
            },
        });

        useEffect(() => {
            if (isPaymentConfirmed && isPaymentReady) {
                onSave();
            }
        }, [isPaymentConfirmed, isPaymentReady, onSave]);

        return (
            <>
                {hasError ? (
                    <CtaButton className={s.button} onClick={handleReturnDeliveryStep} size="large">
                        Return to previous step
                    </CtaButton>
                ) : (
                    <span ref={ref}>
                        {isPaymentConfirmed ? (
                            <CtaButton className={s.button} size="large" isDisabled isLoading>
                                Place my order
                            </CtaButton>
                        ) : (
                            <CtaButton
                                className={classNames(s.amazonButton, s.button, {
                                    [s.amazonButtonFacelift]: isFacelift,
                                })}
                                ariaLabel="Send Order"
                                isDisabled={isLoadingSendOrderButton}
                                isLoading={isLoadingSendOrderButton}
                                onClick={onSend}
                                onHover={handler}
                                size="large"
                            >
                                <Icon className={s.icon} icon={AmazonIcon} />
                            </CtaButton>
                        )}
                    </span>
                )}
            </>
        );
    },
);

AmazonButtons.displayName = 'AmazonButtons';
